"use client";

import Link from "next/link";
import Skeleton from "react-loading-skeleton";
import {
  AccountCircleOutlinedIcon,
  LocalActivityOutlinedIcon,
  RedeemOutlinedIcon,
  RoomServiceOutlinedIcon,
} from "~/icons";
import type { Page } from "~/types";
import { Box } from "../Box/Box";
import styles from "./NavigationBar.module.css";

export function NavigationBar({
  displayMenu,
  activeItem,
  locationRef,
  authenticated,
  userIsLoading,
}: {
  displayMenu: boolean;
  activeItem: Page | undefined;
  locationRef: string;
  authenticated: boolean;
  userIsLoading: boolean;
}): React.ReactNode {
  const accountLabel = authenticated ? "ACCOUNT" : "LOGIN";

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      position="relative"
      paddingX="5"
      paddingY="2"
      className={styles.navBar}
    >
      <Box display="flex" width="full" justifyContent="spaceBetween">
        {displayMenu ? (
          <NavLink
            active={activeItem === "menu"}
            icon="menu"
            label={"MENU"}
            href={`/${locationRef}/menu`}
          />
        ) : null}
        <NavLink
          active={activeItem === "loyalty"}
          icon="promotions"
          label="LOYALTY"
          href={`/${locationRef}/loyalty`}
        />

        <NavLink
          active={activeItem === "promo-code"}
          icon="promo-code"
          label="PROMO CODE"
          href={`/${locationRef}/account/promo-code`}
        />

        {userIsLoading && <NavLinkSkeleton icon="account" />}
        {!userIsLoading && (
          <NavLink
            active={["account", "activity"].includes(activeItem || "")}
            icon="account"
            label={accountLabel}
            href={
              authenticated
                ? `/${locationRef}/account`
                : `/login?next=${encodeURIComponent(`/${locationRef}/menu`)}`
            }
          />
        )}
      </Box>
    </Box>
  );
}

function NavLink({
  active,
  icon,
  label,
  href,
}: {
  active: boolean;
  icon: string;
  label: string;
  href: string;
}): React.ReactNode {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      disabled={active}
    >
      <Link
        href={href}
        className={active ? styles.navBarLinkActive : styles.navBarLink}
        prefetch={false}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginBottom="1"
          width="touchable"
        >
          <NavIcon icon={icon} />
        </Box>
        <Box
          width="touchable"
          display="flex"
          justifyContent="center"
          style={{ whiteSpace: "nowrap" }}
        >
          {label}
        </Box>
      </Link>
    </Box>
  );
}

function NavIcon({ icon }: { icon: string }): React.ReactNode {
  switch (icon) {
    case "menu":
      return <RoomServiceOutlinedIcon width={32} height={32} />;
    case "promotions":
      return <RedeemOutlinedIcon width={32} height={32} />;
    case "promo-code":
      return <LocalActivityOutlinedIcon width={32} height={32} />;
    case "account":
      return <AccountCircleOutlinedIcon width={32} height={32} />;
    default:
      return;
  }
}

function NavLinkSkeleton({ icon }: { icon: string }) {
  return (
    <Box
      className={styles.navBarLink}
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        marginBottom="1"
        width="touchable"
      >
        <NavIcon icon={icon} />
      </Box>
      <Box
        width="touchable"
        display="flex"
        justifyContent="center"
        style={{ whiteSpace: "nowrap" }}
        flexGrow={1}
      >
        <Skeleton width={48} height={23} />
      </Box>
    </Box>
  );
}
